import { Instance as TinyColor } from 'tinycolor2'
import React, { useCallback, useRef, useState } from 'react'
import useStyles from './styles'
import { Button, Stack } from '@mui/material'
import Edit from '../../assets/icons/drawingTools/edit'
import ColorPopover from './colorPopover/colorPopover'
import tinycolor from 'tinycolor2'
import { useDispatch } from 'react-redux'
import commonActions from '../../actions/common.actions'
import { selectCustomColors } from '../../selectors/common'
import { useSelector } from 'react-redux'
import { ChartLineStyle, getLineDashProps, LineStyleProps, LineThickness } from './lineStyle.types'

export type { ChartLineStyle, LineThickness, LineStyleProps }

export type ColorSelectorProps = {
    color: string,
    opacity: number,
    swatchSize?: number,
    compact?: boolean,
    showOnlySwatch?: boolean,
    onColorChange: (color: string, transparency: number) => void
} & LineStyleProps

const LinePreview = ({
    color,
    opacity,
    thickness,
    style,
}: {
    color: string
    opacity: number
    thickness: LineThickness
    style: ChartLineStyle
}) => {
    const c = tinycolor(color)
    c.setAlpha(opacity)
    const { strokeDasharray, strokeLinecap } = getLineDashProps(style)
    return (
        <svg width="28" height="16" style={{ display: 'block', flexShrink: 0 }}>
            <line
                x1="2" y1="8" x2="26" y2="8"
                stroke={c.toRgbString()}
                strokeWidth={thickness}
                strokeDasharray={strokeDasharray}
                strokeLinecap={strokeLinecap ?? 'butt'}
            />
        </svg>
    )
}

const ColorSelector: React.FC<ColorSelectorProps> = ({
    color,
    opacity,
    onColorChange,
    showOnlySwatch = false,
    compact = false,
    lineThickness,
    lineStyle,
    onLineThicknessChange,
    onLineStyleChange,
}) => {
    const colorValue = tinycolor(color)
    colorValue.setAlpha(opacity)
    const { classes, cx } = useStyles({ color: colorValue, showOnlySwatch })
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const customColors = useSelector(selectCustomColors)

    const hasLineThickness = lineThickness !== undefined && onLineThicknessChange
    const hasLineProps = hasLineThickness || (lineStyle !== undefined && onLineStyleChange)
    const onAddCustomColor = useCallback((newColor: TinyColor) => {
        commonActions.addCustomColor(dispatch, newColor.toRgbString())
    }, [])

    const isOpen = Boolean(anchorEl)

    return (
        <div ref={containerRef} className={classes.container}>
            <Button
                className={cx(classes.swatch, { [classes.compact]: compact })}
                size={compact ? 'medium' : 'large'}
                variant="outlined"
                color="inherit"
                style={{
                    padding: '4px',
                    height: '34px',
                    minHeight: '34px',
                    width: hasLineProps ? 'auto' : '34px',
                    lineHeight: 1,
                    boxSizing: 'border-box',
                    gap: hasLineProps ? '4px' : undefined,
                }}
                sx={{
                    ...(isOpen && {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '1px',
                    }),
                }}
                onClick={() => {
                    if (anchorEl) {
                        setAnchorEl(null)
                        return
                    }
                    containerRef.current && setAnchorEl(containerRef.current)
                }}
            >
                <Stack alignItems={'center'} justifyContent={'center'} gap={0.5}>
                    {!showOnlySwatch && <Edit />}
                    <div className={classes.swatchBackground} />
                </Stack>
                {hasLineProps && (
                    <LinePreview
                        color={color}
                        opacity={opacity}
                        thickness={lineThickness ?? 2}
                        style={lineStyle ?? 'solid'}
                    />
                )}
                {!compact && (
                    <span style={{ marginLeft: '8px' }} className={classes.value}>
                        {color} ({(opacity * 100).toFixed(2)}%)
                    </span>
                )}
            </Button>

            {anchorEl && (
                <ColorPopover
                    anchorEl={anchorEl}
                    value={colorValue}
                    onChange={(newValue) => {
                        onColorChange(newValue.toHexString(), newValue.getAlpha())
                    }}
                    onClose={() => setAnchorEl(null)}
                    onAddSwatch={onAddCustomColor}
                    recentSwatches={customColors}
                    lineThickness={lineThickness}
                    lineStyle={lineStyle}
                    onLineThicknessChange={onLineThicknessChange}
                    onLineStyleChange={onLineStyleChange}
                />
            )}
        </div>
    )
}

export default ColorSelector
